import classNames from 'classnames';
import * as React from 'react';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import type { OnlineStudySubject } from '../../../ViewModel/Subjects/OnlineStudySubject.csharp';
import { GetCreditsLabel, GetSubjectsTotalPoints } from '../../Context/OnlineStudyPlanHelpers';
import { Txt } from '../../Txt';
import { Subject } from '../Subject/Subject';
import type { OnlineSemesterItemViewModel } from '../SubjectsBySemester';
import type { IParentItem } from '~/Features/OnlineApplication/Model';
import { Link } from '~/Features/Partials/Link/Link';
import { Button } from '~/Partials/Button/Button';
import { moneyToString } from '~/Partials/Utils/utils';
import styles from './ElectiveItem.module.css';
import { useElectiveItemModel } from './ElectiveItemModel';

export interface ElectiveItemProps {
    parentStudy: ParentStudyViewModel;
    semesterIndex: number;
    isSemesterAdded: boolean;
    item: OnlineSemesterItemViewModel;
    setItemState: (
        item: OnlineSemesterItemViewModel,
        setter: (prevItemState: OnlineSemesterItemViewModel) => void
    ) => void;
}

export const ElectiveItem: React.FC<React.PropsWithChildren<ElectiveItemProps>> = props => {
    const { parentStudy, semesterIndex, isSemesterAdded, item, setItemState } = props;
    const parentItem: IParentItem = {
        semesterIndex,
        itemIndex: item.index,
    };
    const model = useElectiveItemModel({ parentStudy, parentItem, item, setItemState });

    const electivePointsUsed = GetSubjectsTotalPoints(item.selectedElectiveSubjects);
    const creditsLabel = GetCreditsLabel(item.availableElectiveSubjects);

    const renderAvailableElectiveSubject = (subject: OnlineStudySubject) => {
        const isElectiveSubjectSelected =
            item.selectedElectiveSubjects && item.selectedElectiveSubjects.some(s => s.code === subject.code);
        const canBeSelected = electivePointsUsed + subject.credits <= item.electivePointsAvailable;

        const isCheckboxDisabled = isSemesterAdded || (!isElectiveSubjectSelected && !canBeSelected);

        const uuid = `__checkboxUuid:${Math.random()}`;

        return (
            <li key={subject.code} className={styles.ElectiveSubjectItem}>
                <div className={styles.ItemName}>
                    <div className={styles.OrderStatus}>
                        <input
                            type="checkbox"
                            id={uuid}
                            checked={isElectiveSubjectSelected}
                            onChange={e => {
                                if (!e.target.checked) {
                                    model.removeSelectedElectiveSubject(subject);
                                } else if (canBeSelected) {
                                    model.addSelectedElectiveSubject(subject);
                                }
                            }}
                            disabled={isCheckboxDisabled}
                        />
                        <label
                            className={
                                isCheckboxDisabled
                                    ? classNames(styles.HiddenLabel, styles['HiddenLabel--disabled'])
                                    : styles.HiddenLabel
                            }
                            htmlFor={uuid}
                        >
                            <span>{subject.name}</span>
                        </label>
                    </div>
                    <div className={styles.LinkContainer}>
                        <Link href={subject.url} label={subject.name} className={styles.Link} />
                    </div>
                </div>
                <div className={styles.ItemInfo}>
                    <span className={styles.ItemPoints}>
                        {subject.credits} {subject.creditsLabel}
                    </span>
                    <span>{moneyToString(subject.price)}</span>
                </div>
            </li>
        );
    };

    const electiveSubjectsAddedToBasket = model.getItemElectiveSubjectsAddedToBasket();

    return (
        <li>
            {!!electiveSubjectsAddedToBasket && (
                <ul className={styles.SelectedElective}>
                    {electiveSubjectsAddedToBasket.map(subject => (
                        <Subject key={subject.code} {...subject} />
                    ))}
                </ul>
            )}
            <span className={styles.ElectiveInfo}>
                <Txt
                    id="electivePointsInfo"
                    values={{
                        credits: electivePointsUsed,
                        totalCredits: item.electivePointsAvailable,
                        creditsName: creditsLabel,
                    }}
                />
            </span>
            <div className={styles.ElectiveHeader}>
                <Button
                    onClick={() => model.toggleElectiveSubjectsDropdown()}
                    aria-expanded={item.hasOpenedAvailableElectiveSubjects}
                    aria-controls={item.index.toString()}
                >
                    <Txt id="showSubjects" />
                </Button>
            </div>
            {item.hasOpenedAvailableElectiveSubjects && (
                <ul className={styles.ElectiveSubjectsList}>
                    {item.availableElectiveSubjects.map(s => renderAvailableElectiveSubject(s))}
                </ul>
            )}
        </li>
    );
};
